import type { OltVendor } from './olt.js';

/**
 * The generation a chassis belongs to.
 *
 * Lives in shared rather than beside the inventory strategy that first needed
 * it because the browser now asks the same question: which chassis to DRAW for
 * an OLT is the same classification as which transport to read its ONUs with,
 * and two copies of that regex would drift the first time a model string
 * surprised one of them. `services/olt/inventory-strategy.ts` re-exports this,
 * so every existing API import keeps working unchanged.
 */
export type OltFamily = 'c3xx' | 'c6xx' | 'ma56xx' | 'ma58xx' | 'unknown';

/**
 * Single-ONU capabilities a chassis GENERATION may refuse.
 *
 * The same shape as a bulk action id, and kept in the same family-gate table
 * (`services/bulk/vendor-support.ts`), because the question is the same one:
 * "can this generation do this, and if not, what does the operator read?"
 * Only capabilities the server actually refuses somewhere belong here — this
 * is not a feature list, it is the index of the refusals the UI may show
 * before the server would have said the same thing.
 *
 *   onu_static_mgmt_address  a STATIC management address (the ZTE C650 takes
 *                            its management address by DHCP only; the captured
 *                            `wan-ip … mode dhcp` path has no static form)
 *   onu_voip                 the ONU's VoIP service switched from the chassis
 *                            (ZTE C300 `voip protocol` + `sip-service`,
 *                            captured 2026-09-10; the Huawei composition and
 *                            the C650 dialect await a live check)
 *   onu_iptv                 the ONU's IPTV/multicast service switched from
 *                            the chassis (ZTE C300 `iptv service` + `mvlan` +
 *                            `igmp`, captured 2026-09-10; same caveat)
 *   onu_option82             DHCP option 82 on the ONU's subscriber vport
 *                            (ZTE C300 `dhcpv4-l2-relay-agent`, captured
 *                            2026-09-10; the Huawei composition is from the
 *                            vendor reference and awaits a live check, the
 *                            C650 dialect is unprobed)
 *   onu_source_guard         IP source guard on the ONU's subscriber vport
 *                            (ZTE C300 `ip-source-guard`; same caveat)
 *   onu_max_mac_learn        the ONU's MAC-learning limit (ZTE C300
 *                            `security max-mac-learn`; same caveat)
 *   onu_mgmt_ports           the OLT-allocated HTTP/telnet management ports
 *                            for the ONU (ZTE C300 `http|telnet onu-mng-port
 *                            alloc|free`; a Huawei OLT has no counterpart)
 */
/*
  `onu_ip_hosts` (the ONU's own numbered IP interfaces, the C300's `ip-host`
  model, which a C650 does not have) was a row here from 2026-08-27 to
  2026-09-19. It gated authorise and migration on a C650 whenever a management
  VLAN or a routed subscriber VLAN was chosen — so that chassis could only
  take a bridged, unmanaged, OMCI-only authorise. The C6xx composer now
  plans no ip-hosts at all (its management address is the management write's
  `wan-ip … host 2` path and its subscriber WAN is the CPE's over TR-069), so
  no request can reach the refusal, and a row nothing refuses with does not
  belong in the index of refusals.
*/
export const OLT_CAPABILITY_IDS = [
  'onu_static_mgmt_address',
  'onu_voip',
  'onu_iptv',
  'onu_option82',
  'onu_source_guard',
  'onu_max_mac_learn',
  'onu_mgmt_ports',
] as const;
export type OltCapabilityId = (typeof OLT_CAPABILITY_IDS)[number];

/** The generation a chassis belongs to, from the model string it reports. */
export function oltFamily(vendor: OltVendor, model: string | null): OltFamily {
  const text = (model ?? '').toUpperCase();
  if (vendor === 'zte') {
    if (/C6\d\d/.test(text)) return 'c6xx';
    if (/C3\d\d/.test(text)) return 'c3xx';
    return 'unknown';
  }
  if (vendor === 'huawei') {
    if (/MA58/.test(text)) return 'ma58xx';
    if (/MA56/.test(text)) return 'ma56xx';
    return 'unknown';
  }
  return 'unknown';
}
